import { useState, useMemo, useEffect, useRef, useCallback } from 'react'
import { Zap, CheckCircle2 } from 'lucide-react'
import { DeadlineBanner } from './DeadlineBanner'
import { FixtureCard } from './FixtureCard'
import { TeamInfoPopover } from './TeamInfoPopover'
import { SubmitBar } from './SubmitBar'
import { getDisplayName, isSameTeam } from '@/lib/teams'
import type { MatchdayFixture } from '../types'
import type { SeasonSplit } from '@/lib/competitions'

/** User's history with a specific team */
interface TeamHistory {
  picks: number
  wins: number
  draws: number
  losses: number
  pointsEarned: number
}

/** Team's EPL season record */
interface TeamSeasonRecord {
  position: number
  points: number
  wins: number
  draws: number
  losses: number
}

/** TDD usage per season half */
interface TddHalfUsage {
  apertura: number
  clausura: number
}

/** Current selection for this matchday */
interface CurrentSelection {
  team_name: string
  used_double_down: boolean
}

interface EarlyFixtureInfo {
  homeTeam: string
  awayTeam: string
  kickoffTime: Date
}

interface MatchdayMakePickProps {
  matchdayNumber: number | undefined
  season: string
  fixtures: MatchdayFixture[]
  teams?: { name: string; logo: string; code?: string }[]
  teamRanks?: { [teamName: string]: number }
  teamRecords?: { [teamName: string]: TeamSeasonRecord }
  teamHistories?: { [teamName: string]: TeamHistory }
  tddUsageByHalf?: TddHalfUsage
  seasonSplits?: SeasonSplit[]
  deadlineDate?: Date | null
  countdown: {
    formatted: string
    isExpired: boolean
    hasDeadline: boolean
    totalSeconds: number
  }
  earlyFixture?: EarlyFixtureInfo | null
  tddRemaining: number
  currentSelection?: CurrentSelection | null
  getTeamUsage: (teamName: string) => number
  canSelectTeam: (teamName: string) => boolean
  isTddEligible: (teamName: string) => boolean
  canUseTddForTeam: (teamName: string) => boolean
  isSubmitting: boolean
  isCancelling?: boolean
  onSubmit: (teamName: string, tddEnabled: boolean) => void
  onCancelPick?: () => void
}

export function MatchdayMakePick({
  matchdayNumber,
  season,
  fixtures,
  teams,
  teamRanks,
  teamRecords,
  teamHistories,
  tddUsageByHalf,
  seasonSplits,
  deadlineDate,
  countdown,
  earlyFixture,
  tddRemaining,
  currentSelection,
  getTeamUsage,
  canSelectTeam,
  isTddEligible,
  canUseTddForTeam,
  isSubmitting,
  isCancelling,
  onSubmit,
  onCancelPick,
}: MatchdayMakePickProps) {
  const [selectedTeam, setSelectedTeam] = useState<string | null>(null)
  const [tddEnabled, setTddEnabled] = useState(false)
  const [viewingTeam, setViewingTeam] = useState<string | null>(null)

  // Track the selection we initialized from to detect changes vs user actions
  const initializedFromRef = useRef<string | null | undefined>(undefined)

  // Initialize selected team from current selection on mount
  // Only re-initialize if currentSelection changes from server (not user action)
  useEffect(() => {
    // Wait for fixtures to load
    if (!fixtures.length) return

    // Get the team name from currentSelection for comparison
    const currentSelectionTeam = currentSelection?.team_name ?? null

    // If we've already initialized from this selection, don't re-initialize
    // This allows user to change their selection without it snapping back
    if (initializedFromRef.current === currentSelectionTeam) return

    // Mark what we're initializing from
    initializedFromRef.current = currentSelectionTeam

    if (currentSelection) {
      // Find the matching fixture team name using isSameTeam to handle
      // formal vs informal name mismatches (e.g., "Tottenham Hotspur" vs "Tottenham")
      const matchingFixture = fixtures.find(
        (f) =>
          isSameTeam(f.home_team, currentSelection.team_name) ||
          isSameTeam(f.away_team, currentSelection.team_name)
      )
      if (matchingFixture) {
        // Use the fixture's team name (not the stored formal name) so that
        // subsequent comparisons in FixtureCard work correctly
        const teamName = isSameTeam(matchingFixture.home_team, currentSelection.team_name)
          ? matchingFixture.home_team
          : matchingFixture.away_team
        setSelectedTeam(teamName)
        setTddEnabled(currentSelection.used_double_down)
      }
    } else {
      // No current selection - reset state
      setSelectedTeam(null)
      setTddEnabled(false)
    }
  }, [currentSelection, fixtures])

  // Calculate autopick team: always the HIGHEST odds team for the matchday (biggest underdog)
  // Rule: If autopick is used on a team you've already picked twice, you get 0 points
  // So we don't filter by canSelectTeam - we always show the true autopick
  const autopickTeam = useMemo(() => {
    if (fixtures.length === 0) return null

    // Collect all teams with their odds
    const teamsWithOdds: { team: string; odds: number }[] = []
    
    for (const fixture of fixtures) {
      // Add home team if they have odds
      if (fixture.home_win_odds != null) {
        teamsWithOdds.push({ team: fixture.home_team, odds: fixture.home_win_odds })
      }
      // Add away team if they have odds
      if (fixture.away_win_odds != null) {
        teamsWithOdds.push({ team: fixture.away_team, odds: fixture.away_win_odds })
      }
    }

    if (teamsWithOdds.length === 0) return null

    // Sort by odds (highest first = biggest underdog / least likely to win)
    teamsWithOdds.sort((a, b) => b.odds - a.odds)

    // Return the team with the highest odds - this is always the autopick
    return teamsWithOdds[0].team
  }, [fixtures])

  // Determine if the current local selection matches what's already saved on the server
  const pickSaved = useMemo(() => {
    if (!currentSelection || !selectedTeam) return false
    const teamMatches = isSameTeam(selectedTeam, currentSelection.team_name)
    const tddMatches = tddEnabled === currentSelection.used_double_down
    return teamMatches && tddMatches
  }, [currentSelection, selectedTeam, tddEnabled])

  const handleSelectTeam = (teamName: string) => {
    setSelectedTeam(teamName)
    // Restore saved TDD state if re-selecting the same team, otherwise reset
    const isRestoringPick = currentSelection?.team_name && isSameTeam(teamName, currentSelection.team_name)
    setTddEnabled(isRestoringPick ? currentSelection.used_double_down : false)
  }

  const handleDeselectTeam = useCallback(() => {
    setSelectedTeam(null)
    setTddEnabled(false)
  }, [])

  const handleToggleTdd = (enabled: boolean) => {
    setTddEnabled(enabled)
  }

  // Exclude the current matchday's saved pick from team usage so the user
  // can re-select their saved team after deselecting (otherwise it shows "Maxed")
  const getAdjustedUsage = useCallback((teamName: string) => {
    const rawUsage = getTeamUsage(teamName)
    if (currentSelection?.team_name && isSameTeam(teamName, getDisplayName(currentSelection.team_name))) {
      return Math.max(0, rawUsage - 1)
    }
    return rawUsage
  }, [getTeamUsage, currentSelection])

  const handleSubmit = () => {
    if (selectedTeam) {
      onSubmit(selectedTeam, tddEnabled)
    }
  }

  return (
    <div className="min-h-screen bg-zinc-50 dark:bg-zinc-950">
      <div className="max-w-lg mx-auto py-6 pb-[calc(8rem+env(safe-area-inset-bottom))]">
        {/* Deadline Banner */}
        <DeadlineBanner
          matchdayNumber={matchdayNumber}
          season={season}
          deadlineDate={deadlineDate}
          countdown={countdown}
          earlyFixture={earlyFixture}
        />

        {/* TDD Remaining - show for current half */}
        {(() => {
          const splitPoint = seasonSplits?.[0]?.endMatchday ?? 19
          const isFirstHalf = !matchdayNumber || matchdayNumber <= splitPoint
          const label = isFirstHalf
            ? (seasonSplits?.[0]?.name ?? '1H')
            : (seasonSplits?.[1]?.name ?? '2H')
          const usedThisHalf = isFirstHalf
            ? (tddUsageByHalf?.apertura ?? 0)
            : (tddUsageByHalf?.clausura ?? 0)
          const remainingThisHalf = Math.max(0, 2 - usedThisHalf)
          
          return (
            <div className="mt-4 flex items-center justify-between px-3 py-2 rounded-lg bg-violet-50 dark:bg-violet-950/30 border border-violet-200 dark:border-violet-800">
              <div className="flex items-center gap-2">
                <Zap className="w-4 h-4 text-violet-600 dark:text-violet-400" />
                <span className="text-sm font-medium text-violet-700 dark:text-violet-300">
                  DD Available
                </span>
                <span className="text-xs text-violet-500 dark:text-violet-400">
                  ({label})
                </span>
              </div>
              <span className="text-sm font-bold font-mono text-violet-700 dark:text-violet-300">
                {remainingThisHalf}/2
              </span>
            </div>
          )
        })()}

        {/* Confirmation banner when pick is saved */}
        {pickSaved && selectedTeam && (
          <div className="mt-4 flex items-center gap-2.5 px-3 py-2.5 rounded-lg bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800">
            <CheckCircle2 className="w-5 h-5 text-emerald-500 dark:text-emerald-400 shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-emerald-700 dark:text-emerald-300">
                Your Pick: {getDisplayName(selectedTeam)}
                {tddEnabled && (
                  <span className="ml-1.5 inline-flex items-center gap-0.5 text-xs font-bold text-violet-600 dark:text-violet-400">
                    <Zap className="w-3 h-3" /> DD
                  </span>
                )}
              </p>
              <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-0.5">
                Pick saved — no further action needed
              </p>
            </div>
          </div>
        )}

        {/* Fixtures */}
        {fixtures.length > 0 ? (
          <div className="mt-6 space-y-4">
            <h2 className="text-sm font-medium text-zinc-500 dark:text-zinc-400 uppercase tracking-wider">
              {pickSaved ? 'Matchday Fixtures' : 'Select Your Pick'}
            </h2>

            {fixtures.map((fixture) => (
              <FixtureCard
                key={fixture.id}
                fixture={fixture}
                teams={teams}
                homeRank={teamRanks?.[fixture.home_team] || teamRanks?.[getDisplayName(fixture.home_team)]}
                awayRank={teamRanks?.[fixture.away_team] || teamRanks?.[getDisplayName(fixture.away_team)]}
                homeUsage={getAdjustedUsage(getDisplayName(fixture.home_team))}
                awayUsage={getAdjustedUsage(getDisplayName(fixture.away_team))}
                selectedTeam={selectedTeam}
                tddTeam={tddEnabled ? selectedTeam : null}
                tddRemaining={tddRemaining}
                homeTddEligible={isTddEligible(fixture.home_team)}
                awayTddEligible={isTddEligible(fixture.away_team)}
                onSelectTeam={(name) => {
                  if (isCancelling) return
                  const isCurrentSavedPick = currentSelection?.team_name && isSameTeam(name, currentSelection.team_name)
                  if (isCurrentSavedPick || canSelectTeam(getDisplayName(name))) {
                    handleSelectTeam(name)
                  }
                }}
                onDeselectTeam={handleDeselectTeam}
                onToggleTdd={(enabled) => {
                  if (selectedTeam && canUseTddForTeam(selectedTeam)) {
                    handleToggleTdd(enabled)
                  }
                }}
                onViewTeamInfo={setViewingTeam}
              />
            ))}
          </div>
        ) : (
          <div className="mt-8 text-center py-12 text-zinc-500 dark:text-zinc-400">
            <p className="text-lg font-medium">No fixtures available</p>
            <p className="text-sm mt-1">Matchday {matchdayNumber ?? '...'} fixtures are not yet loaded.</p>
          </div>
        )}
      </div>

      {/* Team Info Popover */}
      {viewingTeam && (() => {
        const fixture = fixtures.find(
          (f) => f.home_team === viewingTeam || f.away_team === viewingTeam
        )
        if (!fixture) return null
        const isHome = fixture.home_team === viewingTeam
        const opponent = isHome ? fixture.away_team : fixture.home_team
        const teamDisplayName = getDisplayName(viewingTeam)
        const teamHistory = teamHistories?.[viewingTeam] || teamHistories?.[teamDisplayName]
        const seasonRecord = teamRecords?.[viewingTeam] || teamRecords?.[teamDisplayName]

        return (
          <TeamInfoPopover
            teamName={viewingTeam}
            opponentName={opponent}
            teams={teams}
            isHome={isHome}
            kickoff={fixture.kickoff_time}
            homeOdds={fixture.home_win_odds}
            drawOdds={fixture.draw_odds}
            awayOdds={fixture.away_win_odds}
            usage={getTeamUsage(teamDisplayName)}
            tddEligible={isTddEligible(viewingTeam)}
            seasonRecord={seasonRecord}
            teamHistory={teamHistory}
            onClose={() => setViewingTeam(null)}
          />
        )
      })()}

      {/* Fixed Submit Bar */}
      <SubmitBar
        selectedTeam={selectedTeam}
        tddEnabled={tddEnabled}
        isSubmitting={isSubmitting}
        isCancelling={isCancelling}
        pickSaved={pickSaved}
        hasExistingPick={!!currentSelection}
        existingPickTeam={currentSelection?.team_name ?? null}
        autopickTeam={autopickTeam}
        countdown={countdown}
        onSubmit={handleSubmit}
        onChangePick={handleDeselectTeam}
        onCancelPick={onCancelPick}
      />
    </div>
  )
}
